import { computed, unref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import http from "@/helper/http";
import { RESOURCE_CACHE } from "@/config/queryConfig";

function resolveEntityId(entityId) {
  const resolvedValue = unref(entityId);

  if (resolvedValue && typeof resolvedValue === "object") {
    return (
      resolvedValue.id ||
      resolvedValue.product_id ||
      resolvedValue.service_id ||
      null
    );
  }

  return resolvedValue || null;
}

function resolveEnabled(externalEnabled, entityId) {
  const enabledValue =
    externalEnabled === undefined ? true : Boolean(unref(externalEnabled));
  return enabledValue && Boolean(entityId);
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeSale(sale) {
  const unitPrice = toNumber(sale?.unit_price);
  const quantity = toNumber(sale?.quantity);

  return {
    ...sale,
    unit_price: unitPrice,
    quantity,
    currency: sale?.currency || sale?.currency_code || "RWF",
    total_amount:
      sale?.total_amount != null
        ? toNumber(sale.total_amount)
        : unitPrice * quantity,
  };
}

function normalizePurchase(purchase) {
  const unitPrice = toNumber(purchase?.unit_price);
  const quantity = toNumber(purchase?.quantity);

  return {
    ...purchase,
    unit_price: unitPrice,
    quantity,
    currency: purchase?.currency || purchase?.currency_code || "RWF",
    total_amount:
      purchase?.total_amount != null
        ? toNumber(purchase.total_amount)
        : unitPrice * quantity,
  };
}

/**
 * Fetch product sales and purchases using the existing dedicated endpoints.
 */
export function useProductItemHistory(productId, options = {}) {
  const { enabled: externalEnabled, ...queryOptions } = options;
  const resolvedProductId = computed(() => resolveEntityId(productId));
  const isEnabled = computed(() =>
    resolveEnabled(externalEnabled, resolvedProductId.value),
  );

  const salesQuery = useQuery({
    queryKey: ["productSales", resolvedProductId],
    queryFn: async () => {
      const id = resolvedProductId.value;
      if (!id) return null;

      const response = await http.get(`/v1/store/product/${id}/sales`, {
        params: {
          page: 1,
          per_page: 100,
        },
      });

      return response.data;
    },
    enabled: isEnabled,
    staleTime: RESOURCE_CACHE.products?.staleTime || 10 * 60 * 1000,
    cacheTime: RESOURCE_CACHE.products?.cacheTime || 30 * 60 * 1000,
    ...queryOptions,
  });

  const purchasesQuery = useQuery({
    queryKey: ["productPurchases", resolvedProductId],
    queryFn: async () => {
      const id = resolvedProductId.value;
      if (!id) return null;

      const response = await http.get(
        `/v1/store/product/purchases/product/${id}`,
      );

      return response.data;
    },
    enabled: isEnabled,
    staleTime: RESOURCE_CACHE.products?.staleTime || 10 * 60 * 1000,
    cacheTime: RESOURCE_CACHE.products?.cacheTime || 30 * 60 * 1000,
    ...queryOptions,
  });

  return {
    data: computed(() => {
      const sales = Array.isArray(salesQuery.data.value?.items)
        ? salesQuery.data.value.items.map(normalizeSale)
        : [];
      const purchases = Array.isArray(purchasesQuery.data.value)
        ? purchasesQuery.data.value.map(normalizePurchase)
        : [];
      const costOfGoodsSold = salesQuery.data.value?.cost_of_goods_sold;

      return {
        sales,
        purchases,
        salesTotal: toNumber(salesQuery.data.value?.total) || sales.length,
        purchasesTotal: purchases.length,
        costOfGoodsSold:
          costOfGoodsSold == null ? null : toNumber(costOfGoodsSold),
      };
    }),
    isLoading: computed(
      () => salesQuery.isLoading.value || purchasesQuery.isLoading.value,
    ),
    error: computed(() => salesQuery.error.value || purchasesQuery.error.value),
    refetch: () =>
      Promise.all([salesQuery.refetch(), purchasesQuery.refetch()]),
    salesQuery,
    purchasesQuery,
  };
}

/**
 * Fetch service sales using the dedicated sales endpoint.
 */
export function useServiceItemHistory(serviceId, options = {}) {
  const { enabled: externalEnabled, ...queryOptions } = options;
  const resolvedServiceId = computed(() => resolveEntityId(serviceId));
  const isEnabled = computed(() =>
    resolveEnabled(externalEnabled, resolvedServiceId.value),
  );

  const salesQuery = useQuery({
    queryKey: ["serviceSales", resolvedServiceId],
    queryFn: async () => {
      const id = resolvedServiceId.value;
      if (!id) return null;

      const response = await http.get(`/v1/store/service/${id}/sales`, {
        params: {
          page: 1,
          per_page: 100,
        },
      });

      return response.data;
    },
    enabled: isEnabled,
    staleTime: RESOURCE_CACHE.products?.staleTime || 10 * 60 * 1000,
    cacheTime: RESOURCE_CACHE.products?.cacheTime || 30 * 60 * 1000,
    ...queryOptions,
  });

  return {
    data: computed(() => {
      const sales = Array.isArray(salesQuery.data.value?.items)
        ? salesQuery.data.value.items.map(normalizeSale)
        : [];

      return {
        sales,
        purchases: [],
        salesTotal: toNumber(salesQuery.data.value?.total) || sales.length,
        purchasesTotal: 0,
      };
    }),
    isLoading: computed(() => salesQuery.isLoading.value),
    error: computed(() => salesQuery.error.value),
    refetch: () => salesQuery.refetch(),
    salesQuery,
  };
}
